import { useParams } from "react-router-dom";
import { useBooks, useAuthors, usePeople } from "@/hooks/use-sheets-data";
import { useAnalytics } from "@/hooks/use-analytics";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Bitcoiner } from "@/types";
import { BookInfo } from "@/components/books/book-info";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { XLogo } from "@/components/icons/x-logo";

function extractTweets(html: string): { html: string; url: string | null }[] {
  // Split the HTML by blockquote tags to separate individual tweets
  const tweetBlocks = html.split(/<\/blockquote>/).filter(block => block.includes('<blockquote'));
  
  return tweetBlocks.map(block => {
    const urlMatch = block.match(/href="(https:\/\/twitter\.com\/[^"]+)"/);
    return {
      html: block + '</blockquote>', // Re-add the closing tag
      url: urlMatch ? urlMatch[1] : null
    };
  });
}

export function Book() {
  const { slug } = useParams<{ slug: string }>();
  const { data: books = [], isLoading: booksLoading } = useBooks();
  const { data: authors = [], isLoading: authorsLoading } = useAuthors();
  const { data: people = [], isLoading: peopleLoading } = usePeople();

  useAnalytics();

  const book = books.find((b) => b.slug === slug);
  console.log('Debug - Book finding:', {
    slug,
    booksCount: books.length,
    bookSlugs: books.map(b => b.slug),
    foundBook: book
  });

  const author = book?.authorId ? authors.find((a) => a.id === book.authorId) : undefined;
  console.log('Debug - Author finding:', {
    authorId: book?.authorId,
    authorsCount: authors.length,
    foundAuthor: author
  });
  
  // Find matching people from the recommendedBy array
  const recommenders = book?.recommendedBy
    ? people.filter((person) => {
        const personSlug = person.slug;
        const isRecommender = book.recommendedBy.includes(personSlug);
        console.log(`Debug - Checking recommender ${person.name}:`, {
          personSlug,
          personName: person.name,
          isRecommender,
          recommendedBy: book.recommendedBy,
          matchFound: isRecommender ? 'YES' : 'no'
        });
        return isRecommender;
      })
    : [];
  console.log('Debug - Final recommenders:', {
    bookTitle: book?.title,
    recommendedBy: book?.recommendedBy,
    peopleCount: people.length,
    matchingRecommenders: recommenders.length,
    foundRecommenders: recommenders.map(r => ({
      name: r.name,
      slug: r.slug,
      avatarUrl: r.avatarUrl,
      bio: r.bio
    }))
  });

  if (booksLoading || authorsLoading || peopleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-xl text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-xl text-muted-foreground">Book not found: {slug}</p>
      </div>
    );
  }

  return (
    <div className="-mx-6 -mt-6 min-h-[calc(100vh-4rem)]">
      <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container py-8">
          <div className="flex gap-8">
            <div className="w-48 h-64 relative bg-muted rounded-lg overflow-hidden">
              {book.coverUrl ? (
                <img
                  src={book.coverUrl}
                  alt={book.title}
                  className="object-cover w-full h-full"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                  No cover
                </div>
              )}
            </div>
            <div className="flex-1">
              <BookInfo book={book} author={author} />
            </div>
          </div>
        </div>
      </div>

      <div className="container py-8">
        <div className="grid gap-8 md:grid-cols-[2fr_1fr]">
          {/* Left Column - Scrollable Tweets */}
          {book.tweet && (
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">Recommendations</h2>
              <div className="max-h-[600px] overflow-y-auto pr-4 space-y-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                {extractTweets(book.tweet).map((tweet, index) => (
                  <Card key={index} className="overflow-hidden">
                    <CardContent className="p-6">
                      <div dangerouslySetInnerHTML={{ __html: tweet.html }} />
                      {tweet.url && (
                        <div className="mt-4 flex justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                            className="gap-2"
                          >
                            <a
                              href={tweet.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <XLogo className="h-4 w-4" />
                              View on X
                            </a>
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Right Column - Recommenders */}
          {recommenders.length > 0 && (
            <div>
              <h2 className="text-2xl font-semibold mb-6">Recommended By</h2>
              <div className="grid gap-4">
                {recommenders.map((person: Bitcoiner) => (
                  <Link
                    key={person.id}
                    to={`/people/${person.slug}`}
                    className="block"
                  >
                    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {person.avatarUrl && (
                            <img
                              src={person.avatarUrl}
                              alt={person.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          )}
                          <div>
                            <h3 className="font-semibold">{person.name}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-1">
                              {person.bio}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}